import IconParkOutlineClose from '@/components-v2/icons/IconParkOutlineClose';
import { Backdrop } from '@/components/backdrop';
import transitions from '@/theme/core/transitions';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import React, { ReactNode } from 'react';

interface ModalTransitionsProps {
  open: boolean;
  onChange?: (open: boolean) => void;
  children?: React.ReactNode;
  contentSx?: React.ComponentProps<typeof Box>['sx'];
  hideCloseButton?: boolean;
  hideBackdrop?: boolean;
  disableEscapeKeyDown?: boolean;
}

const ModalTransitionsClose = ({
  onClose,
  hide,
  disableEsc,
}: { onClose?: () => void; hide?: boolean; disableEsc?: boolean }) => {
  if (hide) return null;

  return (
    <IconButton
      onClick={onClose}
      sx={{
        position: 'absolute',
        top: { xs: 12, sm: 16 },
        right: { xs: 12, sm: 16 },
        cursor: 'pointer',
        zIndex: 9999,
        backgroundColor: { xs: 'rgba(245, 245, 245, 0.7)', sm: 'rgba(240, 240, 240, 0.25)' },
        padding: 0.75,
        boxShadow: 'none',
        borderRadius: '50%',
        color: '#333',
        border: { xs: '1px solid rgba(145, 158, 171, 0.7)', sm: '0.75px solid rgba(240, 240, 240, 0.3)' },
        transition: `all ${transitions.durations.short}ms ${transitions.easings.emphasized}`,
        transform: 'rotate(-90deg)',
        '@media (hover: hover)': {
          '&:hover': {
            backgroundColor: 'rgba(235, 235, 235, 0.5)',
            transform: 'rotate(0deg)',
            ':before': {
              opacity: '1',
            },
          },
        },
        ':before': {
          content: { xs: '""', sm: disableEsc ? '""' : '"ESC"' },
          position: 'absolute',
          top: '-13px',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: '10px',
          opacity: '0',
          transition: `opacity ${transitions.durations.short}ms ${transitions.easings.emphasized}`,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          display: { xs: 'none', sm: 'block' },
        },
      }}
    >
      <Box component={IconParkOutlineClose} width={24} height={24} sx={{ fontSize: { xs: 24, sm: 'inherit' } }} />
    </IconButton>
  );
};

export function ModalTransitions({
  children,
  open,
  onChange,
  contentSx,
  hideCloseButton,
  hideBackdrop,
  disableEscapeKeyDown,
}: ModalTransitionsProps) {
  return (
    <Modal
      open={open}
      onClose={
        disableEscapeKeyDown
          ? undefined
          : () => {
              onChange?.(false);
            }
      }
      disableEscapeKeyDown={disableEscapeKeyDown}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      hideBackdrop={hideBackdrop}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& .MuiBackdrop-root': {
          outline: 'none',
        },
      }}
    >
      <Fade in={open} timeout={transitions.durations.overlay}>
        <Box
          sx={{
            maxWidth: '90vw',
            maxHeight: '95vh',
            outline: 'none',
            position: 'relative',
            bgcolor: 'background.paper',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            borderRadius: 4,
            ...contentSx,
          }}
        >
          <ModalTransitionsClose
            onClose={() => onChange?.(false)}
            hide={hideCloseButton}
            disableEsc={disableEscapeKeyDown}
          />
          {children}
        </Box>
      </Fade>
    </Modal>
  );
}

interface ModalTransitionsTitleProps {
  title: ReactNode;
  onClose?: () => void;
}

export function ModalTransitionsTitle({ title }: ModalTransitionsTitleProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Box>{title}</Box>
    </Box>
  );
}
